import React, { useEffect, useState } from 'react';
import InvalidPermissionsDialog from '../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { Role } from '../../lib';
import {
    TabsItemsType,
    TabItems,
    walletsAndGroupsStyles,
    getColumns,
    tableHeight,
    getUserGroupIdName,
    USER_WALLETS,
    USER_GROUPS,
    SYSTEM_WALLETS,
} from './wallets-and-groups-helper';
import { LandingPageWrapper } from 'components';
import { ChainSvg } from '../../assets/images';
import { useUserGroupList, useWalletList } from '../../hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { WALLETS_AND_GROUPS_PATHS } from 'constants/wallets-and-groups-paths';
import { usePermission } from '../../core';
import { GridSortModel } from '@mui/x-data-grid';

export const WalletsAndGroups = (): React.JSX.Element => {
    const { hasUserCurrentRole, hasCurrentPermissionAndRole } = usePermission();
    const [selectedTab, setSelectedTab] = useState<TabsItemsType>(USER_WALLETS);
    const [rowsData, setRowsData] = useState<any>([]);
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const {
        data: walletsData,
        isLoading: walletsLoading,
        refetch: refetchWalletList,
    } = useWalletList();
    const {
        data: userGroupsData,
        isLoading: userGroupsLoading,
        refetch: refetchUserGroupsList,
    } = useUserGroupList();
    const columns = getColumns(selectedTab, readOnly);
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const option = queryParams.get('option');

    useEffect(() => {
        if (option) {
            setSelectedTab(option as TabsItemsType);
        }
    }, [option]);

    const possiblySetReadOnlyModeIfUserHasRoleMissing = (role: Role): void => {
        if (hasUserCurrentRole(role)) {
            setReadOnly(false);
        } else {
            setReadOnly(true);
        }
    };

    const possiblySetReadOnlyModeForCurrentTab = (): void => {
        if (selectedTab === USER_WALLETS) {
            possiblySetReadOnlyModeIfUserHasRoleMissing('sdzAdmin');
        } else {
            possiblySetReadOnlyModeIfUserHasRoleMissing('sdzGovernance');
        }
    };

    useEffect(() => {
        possiblySetReadOnlyModeForCurrentTab();
        if (selectedTab === USER_WALLETS && !walletsLoading) {
            setRowsData(walletsData?.data?.userWallets ?? []);
        } else if (selectedTab === USER_GROUPS && !userGroupsLoading) {
            setRowsData(userGroupsData?.data || []);
        } else if (selectedTab === SYSTEM_WALLETS && !walletsLoading) {
            const rows =
                walletsData?.data?.systemWallets?.map((item: any) => ({
                    id: item?.id,
                    description: item?.description,
                    type: item?.type,
                    accessControlUserGroup: item?.accessControlUserGroup,
                    accessControlUserGroupName: getUserGroupIdName(
                        userGroupsData?.data || [],
                        item?.accessControlUserGroup
                    ),
                    writeUserGroup: item?.writeUserGroup,
                    writeUserGroupName: getUserGroupIdName(
                        userGroupsData?.data || [],
                        item?.writeUserGroup
                    ),
                })) ?? [];
            setRowsData(rows);
        } else {
            setRowsData([]);
        }
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab, walletsLoading, userGroupsLoading]);

    const onTabChange = (event: React.SyntheticEvent, newValue: any): void => {
        if (event) {
            setSelectedTab(newValue);
            setRowsData([]);
            setIsLoading(true);
            if (newValue === USER_WALLETS) {
                refetchWalletList();
            } else if (newValue === USER_GROUPS) {
                refetchUserGroupsList();
            } else if (newValue === SYSTEM_WALLETS) {
                refetchWalletList();
            } else {
                setRowsData([]);
            }
        }
    };

    const onNewButtonClick = (): void => {
        const role: Role =
            selectedTab === USER_WALLETS ? 'sdzAdmin' : 'sdzGovernance';
        const permission = hasCurrentPermissionAndRole('createMetadata', role);
        if (permission) {
            navigate(
                `${WALLETS_AND_GROUPS_PATHS.CREATE_NEW_WALLETS_AND_GROUPS_PATH}?option=${selectedTab}`
            );
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const handleRowClick = (row: any): void => {
        if (selectedTab === USER_WALLETS) {
            navigate(
                WALLETS_AND_GROUPS_PATHS.EDIT_USER_WALLET_PATH + `/${row.id}`
            );
        } else if (selectedTab === SYSTEM_WALLETS) {
            navigate(
                WALLETS_AND_GROUPS_PATHS.EDIT_SYSTEM_WALLET_PATH + `/${row.id}`
            );
        } else {
            navigate(
                WALLETS_AND_GROUPS_PATHS.EDIT_USER_GROUP_PATH + `/${row.id}`
            );
        }
    };

    const getReadOnlyTooltipMessage = (tab: TabsItemsType): string => {
        if (tab === USER_GROUPS) {
            return 'You have view access. If you need to edit a group, please contact your administrator.';
        } else {
            return 'You have view access. If you need to edit a wallet, please contact your administrator.';
        }
    };

    const getSortModel = (): GridSortModel => {
        if (selectedTab === USER_WALLETS) {
            return [{ field: 'fullName', sort: 'asc' }];
        } else if (selectedTab === USER_GROUPS) {
            return [{ field: 'name', sort: 'asc' }];
        } else {
            return [{ field: 'type', sort: 'asc' }];
        }
    };

    return (
        <>
            <LandingPageWrapper
                title={'Wallets & Groups'}
                showAddButton={!readOnly}
                onAddButtonClick={onNewButtonClick}
                tableData={rowsData}
                isLoading={isLoading || walletsLoading || userGroupsLoading}
                columns={columns}
                getRowId={(row: any): string => `${row.id}`}
                onRowClick={(row): void => {
                    handleRowClick(row);
                }}
                paginationPageSizeOptions={[25, 50, 100]}
                paginationPageSize={25}
                emptyStateIcon={ChainSvg}
                emptyStateBodyMessage={
                    'Create a wallet or group to get started.'
                }
                tabItems={TabItems}
                onTabChange={onTabChange}
                selectedTab={selectedTab}
                tabsStyles={walletsAndGroupsStyles.tabsStyles}
                tabItemStyles={walletsAndGroupsStyles.tabItemStyles}
                rowHeight={selectedTab === USER_WALLETS ? 52 : 107}
                autoRowHeight={selectedTab !== USER_WALLETS}
                tableHeight={tableHeight}
                readonly={readOnly}
                readonlyTooltipTitle={getReadOnlyTooltipMessage(selectedTab)}
                sortModel={getSortModel()}
            />
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </>
    );
};
